import { CANVAS_WIDTH } from "../../game/constants";
import type { UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import { easeOutBack } from "../../shared/easing";
import { GAME_FONT } from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface DailyRewardsOpenDeps {
  ctx: CanvasRenderingContext2D;
  canvasHeight: number;
  crateClosedImage: HTMLImageElement | null;
  nowSec: () => number;
  drawCoinIcon: (x: number, y: number, size: number) => void;
  drawGemIcon: (x: number, y: number, size: number) => void;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawDailyRewardsOpenScreen(deps: DailyRewardsOpenDeps, model: RenderModel): UiRects {
  const { ctx, canvasHeight: canvasH, crateClosedImage: crateImg, nowSec, drawCoinIcon, drawGemIcon } = deps;
  const centerX = CANVAS_WIDTH / 2;
  const uiRects: UiRects = {};

  const animPhase = model.dailyRewardAnimPhase;
  const elapsed = performance.now() - model.dailyRewardAnimStartMs;
  const reward = model.dailyRewardClaimedReward;

  // Advance animation phases based on timing
  if (animPhase === "entrance" && elapsed > 500) {
    // Externally transitioned by stateMachine update, but handle render-side timing
  }

  const crateSize = 120;
  const crateX = centerX - crateSize / 2;
  const crateBaseY = canvasH * 0.35;

  if (animPhase === "entrance") {
    // Scale up from 0 with easeOutBack
    const t = Math.min(1, elapsed / 500);
    const scale = t < 1 ? easeOutBack(t) : 1;
    if (crateImg) {
      ctx.save();
      ctx.translate(centerX, crateBaseY + crateSize / 2);
      ctx.scale(scale, scale);
      ctx.drawImage(crateImg, -crateSize / 2, -crateSize / 2, crateSize, crateSize);
      ctx.restore();
    }
  } else if (animPhase === "waiting") {
    // Bob and draw crate
    const bobY = Math.sin(nowSec() * 2) * 4;
    if (crateImg) {
      ctx.drawImage(crateImg, crateX, crateBaseY + bobY, crateSize, crateSize);
    }
    // "TAP TO OPEN" pulsing
    const pulse = 0.5 + 0.5 * Math.sin(nowSec() * 3);
    ctx.fillStyle = `rgba(100,246,255,${(0.6 + pulse * 0.4).toFixed(3)})`;
    ctx.font = `bold 10px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("TAP TO OPEN", centerX, crateBaseY + crateSize + 20);

    // Full screen tap target
    uiRects.dailyRewardCrateTap = { x: 0, y: 0, w: CANVAS_WIDTH, h: canvasH };
  } else if (animPhase === "shaking") {
    // Wobble with increasing intensity
    const t = Math.min(1, elapsed / 800);
    const intensity = 2 + t * 6;
    const freq = 20 + t * 30;
    const angle = (Math.sin(elapsed / 1000 * freq) * intensity * Math.PI) / 180;
    const scale = 1 + t * 0.05;
    if (crateImg) {
      ctx.save();
      ctx.translate(centerX, crateBaseY + crateSize / 2);
      ctx.rotate(angle);
      ctx.scale(scale, scale);
      ctx.drawImage(crateImg, -crateSize / 2, -crateSize / 2, crateSize, crateSize);
      ctx.restore();
    }
  } else if (animPhase === "burst") {
    const t = Math.min(1, elapsed / 300);
    // White flash
    if (t < 0.5) {
      const flashA = 0.6 * (1 - t / 0.5);
      ctx.fillStyle = `rgba(255,255,255,${flashA.toFixed(3)})`;
      ctx.fillRect(0, 0, CANVAS_WIDTH, canvasH);
    }
    // Particles flying out
    for (let i = 0; i < 16; i++) {
      const angle = (i / 16) * Math.PI * 2 + i * 0.3;
      const dist = t * 80 + i * 3;
      const px = centerX + Math.cos(angle) * dist;
      const py = crateBaseY + crateSize / 2 + Math.sin(angle) * dist + t * t * 40;
      const size = 4 * (1 - t * 0.5);
      const colors = ["#ffd700", "#64f6ff", "#ff8844", "#ffffff"];
      ctx.fillStyle = colors[i % colors.length]!;
      ctx.globalAlpha = 1 - t;
      ctx.fillRect(px - size / 2, py - size / 2, size, size);
    }
    ctx.globalAlpha = 1;
  } else if (animPhase === "reveal" || animPhase === "done") {
    const revealT = animPhase === "done" ? 1 : Math.min(1, elapsed / 800);

    if (reward) {
      const iconSz = 40;
      const crateCenterY = crateBaseY + crateSize / 2;
      const spacing = 50;

      // Coins -- pop from crate center, drift left
      const coinT = Math.min(1, revealT * 2);
      const coinScale = coinT < 1 ? easeOutBack(coinT) : 1;
      const coinDriftX = coinT * -spacing;
      const coinDriftY = coinT * -30;
      ctx.save();
      ctx.translate(centerX + coinDriftX, crateCenterY + coinDriftY);
      ctx.scale(coinScale, coinScale);
      drawCoinIcon(-iconSz / 2, -iconSz / 2, iconSz);
      ctx.fillStyle = "#ffd700";
      ctx.font = `bold 14px ${GAME_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(`+${reward.coins}`, 0, iconSz / 2 + 4);
      ctx.restore();

      // Gems -- pop from crate center, drift right (staggered)
      const gemT = Math.min(1, Math.max(0, revealT * 2 - 0.4));
      const gemScale = gemT < 1 ? easeOutBack(gemT) : 1;
      const gemDriftX = gemT * spacing;
      const gemDriftY = gemT * -30;
      ctx.save();
      ctx.translate(centerX + gemDriftX, crateCenterY + gemDriftY);
      ctx.scale(gemScale, gemScale);
      drawGemIcon(-iconSz / 2, -iconSz / 2, iconSz);
      ctx.fillStyle = "#64f6ff";
      ctx.font = `bold 14px ${GAME_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(`+${reward.gems}`, 0, iconSz / 2 + 4);
      ctx.restore();
    }

    if (animPhase === "done") {
      const fadeT = Math.min(1, elapsed / 600);
      ctx.globalAlpha = fadeT;
      ctx.fillStyle = "#e9f7ff";
      ctx.font = `bold 8px ${GAME_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText("TAP TO CONTINUE", centerX, crateBaseY + 90);
      ctx.globalAlpha = 1;

      uiRects.dailyRewardContinue = { x: 0, y: 0, w: CANVAS_WIDTH, h: canvasH };
    }
  }

  return uiRects;
}
